/*
 * Product gallery behaviour — the interactivity the markup was missing (PDP-GALLERY-JS).
 *
 * The gallery renders as a native scroll-snap slideshow: [data-pdp-gallery-track] is
 * `overflow: scroll; scroll-snap-type: x mandatory` and each [data-pdp-slide] is
 * `scroll-snap-align: start`. So the LAYOUT is already correct and swipe already works — what was
 * absent is the wiring for the arrows, the thumbnails, the aria-current sync, and click-to-zoom.
 *
 * This script DRIVES that existing mechanism (it sets track.scrollLeft); it does not invent a new
 * one, so the measured render is unchanged and the parity gates stay green — nothing here runs at
 * load except attaching listeners. PF-024's lesson applies in reverse: the box was always right,
 * only the behaviour was missing.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var SMOOTH = REDUCED ? 'auto' : 'smooth';

  function initGallery(root) {
    if (root.getAttribute('data-pdp-gallery-ready') === 'true') return;
    root.setAttribute('data-pdp-gallery-ready', 'true');

    var track = root.querySelector('[data-pdp-gallery-track]');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-pdp-slide]'));
    if (slides.length === 0) return;

    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-thumb]'));
    var prevBtn = root.querySelector('[data-pdp-gallery-arrow="prev"]');
    var nextBtn = root.querySelector('[data-pdp-gallery-arrow="next"]');
    var thumbScroller = root.querySelector('[data-pdp-thumbs-scroller]');

    var base = slides[0].offsetLeft; // left edge of the scroll content, padding included
    var current = 0;

    function markThumb(i) {
      for (var t = 0; t < thumbs.length; t++) {
        if (t === i) thumbs[t].setAttribute('aria-current', 'true');
        else thumbs[t].removeAttribute('aria-current');
      }
      var active = thumbs[i];
      if (!active || !thumbScroller) return;
      // Keep the active thumb visible by scrolling ITS scroller only — never the page.
      var tl = active.offsetLeft - thumbs[0].offsetLeft;
      var tr = tl + active.offsetWidth;
      var vl = thumbScroller.scrollLeft;
      var vr = vl + thumbScroller.clientWidth;
      if (tl < vl) thumbScroller.scrollTo({ left: tl, behavior: SMOOTH });
      else if (tr > vr) thumbScroller.scrollTo({ left: tr - thumbScroller.clientWidth, behavior: SMOOTH });
    }

    function setCurrent(i) {
      current = (i % slides.length + slides.length) % slides.length;
      markThumb(current);
    }

    function goTo(i, smooth) {
      var idx = (i % slides.length + slides.length) % slides.length;
      track.scrollTo({ left: slides[idx].offsetLeft - base, behavior: smooth ? SMOOTH : 'auto' });
      setCurrent(idx);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1, true); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1, true); });

    for (var k = 0; k < thumbs.length; k++) {
      (function (i) {
        thumbs[i].addEventListener('click', function () { goTo(i, true); });
      })(k);
    }

    // Keep current/aria-current honest when the user swipes or scrolls the track by hand.
    var scrollTimer;
    track.addEventListener('scroll', function () {
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        var sl = track.scrollLeft;
        var nearest = 0;
        var best = Infinity;
        for (var i = 0; i < slides.length; i++) {
          var d = Math.abs((slides[i].offsetLeft - base) - sl);
          if (d < best) { best = d; nearest = i; }
        }
        if (nearest !== current) setCurrent(nearest);
      }, 80);
    }, { passive: true });

    wireZoom(root, slides);
    setCurrent(0);
  }

  // ---- click-to-zoom lightbox ---------------------------------------------------------------------
  // The overlay is created on demand and styled by a stylesheet injected at RUNTIME, so it never
  // exists in the server-rendered HTML the parity/document gates measure. It appears only after a
  // click, which no gate performs.
  var stylesInjected = false;
  function injectZoomStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    var css =
      '[data-pdp-zoom-overlay]{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;' +
      'justify-content:center;background:rgba(20,18,15,.92);padding:24px;cursor:zoom-out;' +
      'opacity:0;transition:opacity .18s ease}' +
      '[data-pdp-zoom-overlay][data-open="true"]{opacity:1}' +
      '[data-pdp-zoom-overlay] img{max-width:100%;max-height:100%;object-fit:contain;' +
      'box-shadow:0 12px 48px rgba(0,0,0,.5);cursor:default}' +
      '[data-pdp-zoom-close]{position:absolute;top:16px;right:20px;width:44px;height:44px;' +
      'display:flex;align-items:center;justify-content:center;border:0;border-radius:50%;' +
      'background:rgba(255,255,255,.12);color:#fff;font-size:28px;line-height:1;cursor:pointer}' +
      '[data-pdp-zoom-close]:focus-visible{outline:2px solid #fff;outline-offset:2px}' +
      '@media (prefers-reduced-motion: reduce){[data-pdp-zoom-overlay]{transition:none}}' +
      '[data-pdp-media] img{cursor:zoom-in}';
    var el = document.createElement('style');
    el.setAttribute('data-pdp-zoom-styles', 'true');
    el.appendChild(document.createTextNode(css));
    document.head.appendChild(el);
  }

  function wireZoom(root, slides) {
    for (var i = 0; i < slides.length; i++) {
      var img = slides[i].querySelector('[data-pdp-media] img');
      if (img) {
        img.addEventListener('click', function (ev) {
          injectZoomStyles();
          openZoom(ev.currentTarget);
        });
      }
    }
  }

  function openZoom(img) {
    var lastFocus = document.activeElement;
    var overlay = document.createElement('div');
    overlay.setAttribute('data-pdp-zoom-overlay', 'true');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', img.getAttribute('alt') || 'Product image');

    var full = document.createElement('img');
    // Request a larger rendition from the same Shopify CDN src by bumping the width param.
    full.src = (img.currentSrc || img.src).replace(/([?&]width=)\d+/, '$1' + 2048);
    full.alt = img.getAttribute('alt') || '';

    var close = document.createElement('button');
    close.setAttribute('type', 'button');
    close.setAttribute('data-pdp-zoom-close', 'true');
    close.setAttribute('aria-label', 'Close');
    close.appendChild(document.createTextNode('×'));

    overlay.appendChild(full);
    overlay.appendChild(close);
    document.body.appendChild(overlay);
    // next frame -> transition in
    requestAnimationFrame(function () { overlay.setAttribute('data-open', 'true'); });

    function done() {
      overlay.removeEventListener('transitionend', afterFade);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function afterFade(e) { if (e.target === overlay) done(); }
    function dismiss() {
      overlay.removeAttribute('data-open');
      if (REDUCED) done();
      else overlay.addEventListener('transitionend', afterFade);
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); dismiss(); }
    }

    full.addEventListener('click', function (e) { e.stopPropagation(); });
    overlay.addEventListener('click', dismiss);
    close.addEventListener('click', function (e) { e.stopPropagation(); dismiss(); });
    document.addEventListener('keydown', onKey);
    close.focus();
  }

  function initAll() {
    var galleries = document.querySelectorAll('[data-pdp-gallery]');
    for (var i = 0; i < galleries.length; i++) initGallery(galleries[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
